import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  Collapse,
  AlertTitle,
  Link,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Help as HelpIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lightbulb as LightbulbIcon,
} from "@mui/icons-material";
import api from "../services/api";
import { decryptField } from "../utils/encryption";

// VAT period presets for Ireland
const VAT_PERIODS = {
  biMonthly: [
    { label: "Jan-Feb", startMonth: 0, endMonth: 1 },
    { label: "Mar-Apr", startMonth: 2, endMonth: 3 },
    { label: "May-Jun", startMonth: 4, endMonth: 5 },
    { label: "Jul-Aug", startMonth: 6, endMonth: 7 },
    { label: "Sep-Oct", startMonth: 8, endMonth: 9 },
    { label: "Nov-Dec", startMonth: 10, endMonth: 11 },
  ],
  quarterly: [
    { label: "Q1 (Jan-Mar)", startMonth: 0, endMonth: 2 },
    { label: "Q2 (Apr-Jun)", startMonth: 3, endMonth: 5 },
    { label: "Q3 (Jul-Sep)", startMonth: 6, endMonth: 8 },
    { label: "Q4 (Oct-Dec)", startMonth: 9, endMonth: 11 },
  ],
};

function SummaryCard({ title, value, subtitle, icon, color = "primary" }) {
  return (
    <Card sx={{ height: "100%", borderRadius: 3 }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} color={`${color}.main`}>
              €{value.toLocaleString("en-IE", { minimumFractionDigits: 2 })}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: `${color}.lighter`,
              borderRadius: 2,
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function VATReturnsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vatData, setVatData] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Period selection state
  const [periodType, setPeriodType] = useState("biMonthly");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedPeriod, setSelectedPeriod] = useState(0);

  // Custom date range
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [useCustomDates, setUseCustomDates] = useState(false);

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [vatSettings, setVatSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [newRate, setNewRate] = useState({
    name: "",
    rate: "",
    description: "",
  });

  // Historical returns state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historicalReturns, setHistoricalReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [savingReturn, setSavingReturn] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Quick guide state
  const [showQuickGuide, setShowQuickGuide] = useState(true);

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const getDateRange = () => {
    if (useCustomDates && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }

    const periods = VAT_PERIODS[periodType];
    const period = periods[selectedPeriod];

    const startDate = new Date(selectedYear, period.startMonth, 1);
    const endDate = new Date(selectedYear, period.endMonth + 1, 0);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const fetchVATData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRange();
      const response = await api.get("/vat/summary", {
        params: { startDate, endDate },
      });
      setVatData(response.data);
    } catch (err) {
      console.error("Failed to fetch VAT data:", err);
      setError("Failed to load VAT data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVATData();
    fetchSettings();
  }, [selectedYear, selectedPeriod, periodType]);

  // Fetch VAT settings
  const fetchSettings = async () => {
    try {
      const response = await api.get("/vat/settings");
      setVatSettings(response.data);
    } catch (err) {
      console.error("Failed to fetch VAT settings:", err);
    }
  };

  // Save VAT settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post("/vat/settings", vatSettings);
      setSnackbar({
        open: true,
        message: "Settings saved successfully",
        severity: "success",
      });
      setSettingsOpen(false);
    } catch (err) {
      console.error("Failed to save VAT settings:", err);
      setSnackbar({
        open: true,
        message: "Failed to save settings",
        severity: "error",
      });
    } finally {
      setSavingSettings(false);
    }
  };

  // Fetch historical VAT returns
  const fetchHistoricalReturns = async () => {
    setLoadingReturns(true);
    try {
      const response = await api.get("/vat/returns");
      setHistoricalReturns(response.data.returns || []);
    } catch (err) {
      console.error("Failed to fetch historical returns:", err);
    } finally {
      setLoadingReturns(false);
    }
  };

  // Save current period as a VAT return
  const saveCurrentReturn = async (status = "draft") => {
    if (!vatData) return;

    setSavingReturn(true);
    try {
      const { startDate, endDate } = getDateRange();
      await api.post("/vat/returns", {
        periodStartDate: startDate,
        periodEndDate: endDate,
        vatCollected: vatData.summary.vatCollected,
        vatPaid: vatData.summary.vatPaid,
        vatLiability: vatData.summary.vatLiability,
        salesTotal: vatData.summary.salesTotal,
        purchasesTotal: vatData.summary.purchasesTotal,
        salesCount: vatData.breakdown.sales.count,
        purchasesCount: vatData.breakdown.purchases.count,
        status,
      });
      setSnackbar({
        open: true,
        message:
          status === "filed"
            ? "VAT return marked as filed"
            : "VAT return saved as draft",
        severity: "success",
      });
      fetchHistoricalReturns();
    } catch (err) {
      console.error("Failed to save VAT return:", err);
      setSnackbar({
        open: true,
        message: "Failed to save VAT return",
        severity: "error",
      });
    } finally {
      setSavingReturn(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const response = await api.get("/vat/report", {
        params: { startDate, endDate },
        responseType: "blob",
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `VAT_Report_${vatData.period.startDate}_${vatData.period.endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      setSnackbar({
        open: true,
        message: "Failed to download report.",
        severity: "error",
      });
    }
  };

  // Update a historical return (e.g., mark as filed)
  const updateReturn = async (id, status) => {
    try {
      await api.put(`/vat/returns/${id}`, { status });
      setSnackbar({
        open: true,
        message: "VAT return updated",
        severity: "success",
      });
      fetchHistoricalReturns();
    } catch (err) {
      console.error("Failed to update VAT return:", err);
      setSnackbar({
        open: true,
        message: "Failed to update VAT return",
        severity: "error",
      });
    }
  };

  // Delete a historical return
  const deleteReturn = async (id) => {
    try {
      await api.delete(`/vat/returns/${id}`);
      setSnackbar({
        open: true,
        message: "VAT return deleted",
        severity: "success",
      });
      fetchHistoricalReturns();
    } catch (err) {
      console.error("Failed to delete VAT return:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete VAT return",
        severity: "error",
      });
    }
  };

  // Handle opening history dialog
  const handleOpenHistory = () => {
    setHistoryOpen(true);
    fetchHistoricalReturns();
  };

  // Add a new VAT rate
  const addVatRate = () => {
    if (!newRate.name || newRate.rate === "") return;
    const id = newRate.name.toLowerCase().replace(/\s+/g, "_");
    setVatSettings({
      ...vatSettings,
      vatRates: [
        ...(vatSettings.vatRates || []),
        { ...newRate, id, rate: parseFloat(newRate.rate) },
      ],
    });
    setNewRate({ name: "", rate: "", description: "" });
  };

  // Delete a VAT rate
  const deleteVatRate = (id) => {
    setVatSettings({
      ...vatSettings,
      vatRates: vatSettings.vatRates.filter((r) => r.id !== id),
    });
  };

  // Update a VAT rate
  const updateVatRate = (id, field, value) => {
    setVatSettings({
      ...vatSettings,
      vatRates: vatSettings.vatRates.map((r) =>
        r.id === id
          ? { ...r, [field]: field === "rate" ? parseFloat(value) : value }
          : r
      ),
    });
  };

  const handleCustomDateSearch = () => {
    if (customStartDate && customEndDate) {
      setUseCustomDates(true);
      fetchVATData();
    }
  };

  const handlePresetPeriodChange = () => {
    setUseCustomDates(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount).toLocaleString("en-IE", {
      minimumFractionDigits: 2,
    })}`;
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            VAT Returns
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Calculate your VAT liability for Revenue. Select a period to see
            your VAT summary.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleOpenHistory}
          >
            History
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </Button>
        </Box>
      </Box>

      {/* Quick Guide for Irish VAT */}
      <Paper
        sx={{
          p: 0,
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "primary.light",
          background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
          }}
          onClick={() => setShowQuickGuide(!showQuickGuide)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LightbulbIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Quick Guide: VAT Returns for Irish Contractors
            </Typography>
          </Stack>
          <IconButton size="small">
            {showQuickGuide ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={showQuickGuide}>
          <Divider />
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  📊 How It Works
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • <strong>Output VAT</strong>: VAT you charge on invoices (you
                  owe this to Revenue)
                  <br />• <strong>Input VAT</strong>: VAT you pay on expenses
                  (you can claim this back)
                  <br />• <strong>VAT Liability</strong>: Output - Input = what
                  you pay/claim
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  📅 Filing Deadlines
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • <strong>Bi-monthly</strong>: Due 23rd of month after period
                  ends
                  <br />• <strong>Example</strong>: Jan-Feb return due by 23rd
                  March
                  <br />• <strong>Annual</strong>: If turnover under €3m, you
                  may qualify
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  🚀 Next Steps
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                    href="https://www.ros.ie"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    File on ROS (Revenue)
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Use the figures below to complete your VAT3 return on ROS
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Period Selection */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Select VAT Period
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Period Type</InputLabel>
              <Select
                value={periodType}
                label="Period Type"
                onChange={(e) => {
                  setPeriodType(e.target.value);
                  setSelectedPeriod(0);
                  handlePresetPeriodChange();
                }}
              >
                <MenuItem value="biMonthly">Bi-Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  handlePresetPeriodChange();
                }}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={(e) => {
                  setSelectedPeriod(e.target.value);
                  handlePresetPeriodChange();
                }}
              >
                {VAT_PERIODS[periodType].map((p, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<CalendarIcon />}
              onClick={() => setUseCustomDates(!useCustomDates)}
            >
              Custom Dates
            </Button>
          </Grid>
        </Grid>

        {useCustomDates && (
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCustomDateSearch}
                  disabled={!customStartDate || !customEndDate}
                >
                  Apply Custom Dates
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* VAT Summary */}
      {!loading && vatData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SummaryCard
                title="VAT Collected (Output VAT)"
                value={vatData.summary.vatCollected}
                subtitle={`From ${vatData.breakdown.sales.count} invoice(s)`}
                icon={<TrendingUpIcon sx={{ color: "success.main" }} />}
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SummaryCard
                title="VAT Paid (Input VAT)"
                value={vatData.summary.vatPaid}
                subtitle={`From ${vatData.breakdown.purchases.count} expense(s)`}
                icon={<TrendingDownIcon sx={{ color: "warning.main" }} />}
                color="warning"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SummaryCard
                title={
                  vatData.summary.vatLiability >= 0
                    ? "VAT Owed to Revenue"
                    : "VAT Refund Due"
                }
                value={Math.abs(vatData.summary.vatLiability)}
                subtitle={
                  vatData.summary.vatLiability >= 0
                    ? "Amount payable"
                    : "Amount claimable"
                }
                icon={
                  <AccountBalanceIcon
                    sx={{
                      color:
                        vatData.summary.vatLiability >= 0
                          ? "error.main"
                          : "info.main",
                    }}
                  />
                }
                color={vatData.summary.vatLiability >= 0 ? "error" : "info"}
              />
            </Grid>
          </Grid>

          {/* VAT3 Form Summary - Ready for ROS */}
          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              border: "2px solid",
              borderColor: "primary.main",
              background: "linear-gradient(135deg, #fefce8 0%, #fff 100%)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Typography variant="h6" fontWeight={700}>
                📋 Your VAT3 Return Summary
              </Typography>
              <Chip label="Copy these to ROS" size="small" color="primary" />
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Use these figures to complete your VAT3 return on Revenue Online
              Service (ROS)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: "background.paper" }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    Period
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatDate(vatData.period.startDate)} -{" "}
                    {formatDate(vatData.period.endDate)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: "background.paper" }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    T1: VAT on Sales (Output VAT)
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="text.primary"
                  >
                    {formatCurrency(vatData.summary.vatCollected)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: "background.paper" }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    T2: VAT on Purchases (Input VAT)
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color="text.primary"
                  >
                    {formatCurrency(vatData.summary.vatPaid)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor:
                      vatData.summary.vatLiability >= 0
                        ? "error.50"
                        : "success.50",
                    borderColor:
                      vatData.summary.vatLiability >= 0
                        ? "error.main"
                        : "success.main",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    T3:{" "}
                    {vatData.summary.vatLiability >= 0
                      ? "VAT Payable"
                      : "VAT Refundable"}
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    color={
                      vatData.summary.vatLiability >= 0
                        ? "error.main"
                        : "success.main"
                    }
                  >
                    {formatCurrency(Math.abs(vatData.summary.vatLiability))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {vatData.summary.vatLiability >= 0
                      ? "Amount you need to pay to Revenue"
                      : "Amount you can claim back from Revenue"}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    E1: Total Sales (Net, excluding VAT)
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatCurrency(vatData.summary.salesTotal)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    E2: Total Purchases (Net, excluding VAT)
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {formatCurrency(vatData.summary.purchasesTotal)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Ready to File?</AlertTitle>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <Typography variant="body2">
                  Log into ROS with your digital certificate and enter the
                  values above into your VAT3 form.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  href="https://www.ros.ie"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to ROS
                </Button>
              </Stack>
            </Alert>
          </Paper>

          {/* Transaction Details Tabs */}
          <Paper sx={{ borderRadius: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab
                  icon={<DescriptionIcon />}
                  iconPosition="start"
                  label={`Sales (${vatData.breakdown.sales.count})`}
                />
                <Tab
                  icon={<ReceiptIcon />}
                  iconPosition="start"
                  label={`Purchases (${vatData.breakdown.purchases.count})`}
                />
              </Tabs>
            </Box>

            {/* Sales Tab */}
            {tabValue === 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Net Amount</TableCell>
                      <TableCell align="right">VAT Rate</TableCell>
                      <TableCell align="right">VAT Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vatData.breakdown.sales.transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No invoices found for this period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      vatData.breakdown.sales.transactions.map((tx) => (
                        <TableRow key={tx.id} hover>
                          <TableCell>{formatDate(tx.date)}</TableCell>
                          <TableCell>{tx.description}</TableCell>
                          <TableCell>
                            {decryptField(tx.clientName) || tx.clientName}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(tx.netAmount)}
                          </TableCell>
                          <TableCell align="right">{tx.vatRate}%</TableCell>
                          <TableCell align="right">
                            {formatCurrency(tx.vatAmount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={tx.status}
                              color={
                                tx.status === "paid" ? "success" : "warning"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Purchases Tab */}
            {tabValue === 1 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Net Amount</TableCell>
                      <TableCell align="right">VAT Rate</TableCell>
                      <TableCell align="right">VAT Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vatData.breakdown.purchases.transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No expenses found for this period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      vatData.breakdown.purchases.transactions.map((tx) => (
                        <TableRow key={tx.id} hover>
                          <TableCell>{formatDate(tx.date)}</TableCell>
                          <TableCell>{tx.description}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={tx.category}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(tx.netAmount)}
                          </TableCell>
                          <TableCell align="right">{tx.vatRate}%</TableCell>
                          <TableCell align="right">
                            {formatCurrency(tx.vatAmount)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* Action Buttons */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={() => saveCurrentReturn("draft")}
                disabled={savingReturn}
              >
                Save as Draft
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => saveCurrentReturn("filed")}
                disabled={savingReturn}
              >
                Mark as Filed
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="outlined" startIcon={<DownloadIcon />}>
                Export to CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadReport}
              >
                Download VAT Report
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* VAT Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>VAT Settings</DialogTitle>
        <DialogContent dividers>
          {vatSettings && (
            <Box>
              <Typography variant="h6" gutterBottom>
                General Settings
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={vatSettings.vatRegistered}
                        onChange={(e) =>
                          setVatSettings({
                            ...vatSettings,
                            vatRegistered: e.target.checked,
                          })
                        }
                      />
                    }
                    label="VAT Registered"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="VAT Number"
                    fullWidth
                    value={vatSettings.vatNumber || ""}
                    onChange={(e) =>
                      setVatSettings({
                        ...vatSettings,
                        vatNumber: e.target.value,
                      })
                    }
                    placeholder="IE1234567A"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Filing Frequency</InputLabel>
                    <Select
                      value={vatSettings.filingFrequency || "biMonthly"}
                      label="Filing Frequency"
                      onChange={(e) =>
                        setVatSettings({
                          ...vatSettings,
                          filingFrequency: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="biMonthly">Bi-Monthly</MenuItem>
                      <MenuItem value="quarterly">Quarterly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default VAT Rate</InputLabel>
                    <Select
                      value={vatSettings.defaultRate || "standard"}
                      label="Default VAT Rate"
                      onChange={(e) =>
                        setVatSettings({
                          ...vatSettings,
                          defaultRate: e.target.value,
                        })
                      }
                    >
                      {(vatSettings.vatRates || []).map((rate) => (
                        <MenuItem key={rate.id} value={rate.id}>
                          {rate.name} ({rate.rate}%)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                VAT Rates
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Rate (%)</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(vatSettings.vatRates || []).map((rate) => (
                      <TableRow key={rate.id}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={rate.name}
                            onChange={(e) =>
                              updateVatRate(rate.id, "name", e.target.value)
                            }
                            variant="standard"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={rate.rate}
                            onChange={(e) =>
                              updateVatRate(rate.id, "rate", e.target.value)
                            }
                            variant="standard"
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={rate.description || ""}
                            onChange={(e) =>
                              updateVatRate(
                                rate.id,
                                "description",
                                e.target.value
                              )
                            }
                            variant="standard"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteVatRate(rate.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="New rate name"
                          value={newRate.name}
                          onChange={(e) =>
                            setNewRate({ ...newRate, name: e.target.value })
                          }
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="0"
                          value={newRate.rate}
                          onChange={(e) =>
                            setNewRate({ ...newRate, rate: e.target.value })
                          }
                          variant="standard"
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Description"
                          value={newRate.description}
                          onChange={(e) =>
                            setNewRate({
                              ...newRate,
                              description: e.target.value,
                            })
                          }
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={addVatRate}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button
            onClick={saveSettings}
            variant="contained"
            disabled={savingSettings}
          >
            {savingSettings ? <CircularProgress size={20} /> : "Save Settings"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Historical Returns Dialog */}
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Historical VAT Returns</DialogTitle>
        <DialogContent dividers>
          {loadingReturns ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historicalReturns.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No saved VAT returns yet. Save your first return from the current
              period.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">VAT Collected</TableCell>
                    <TableCell align="right">VAT Paid</TableCell>
                    <TableCell align="right">Liability</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Filed Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historicalReturns.map((ret) => (
                    <TableRow key={ret.id}>
                      <TableCell>
                        {formatDate(ret.periodStartDate)} -{" "}
                        {formatDate(ret.periodEndDate)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(ret.vatCollected)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(ret.vatPaid)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          color={
                            ret.vatLiability >= 0
                              ? "error.main"
                              : "success.main"
                          }
                          fontWeight={600}
                        >
                          {formatCurrency(Math.abs(ret.vatLiability))}
                          {ret.vatLiability < 0 && " (Refund)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ret.status === "filed" ? "Filed" : "Draft"}
                          color={ret.status === "filed" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {ret.filedAt ? formatDate(ret.filedAt) : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {ret.status !== "filed" && (
                          <Tooltip title="Mark as Filed">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => updateReturn(ret.id, "filed")}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteReturn(ret.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
